import React, { FC, useEffect, useMemo, useState } from 'react';
import { TimeRange } from 'common/components/index';
import { OpeningHours, TimeSlots } from 'common/types/clinics';
import {
  Wrapper,
  SlotsWrapper,
  ScheduleFieldItem,
  SlotsContainer,
  ReplicateButton,
} from './styles';
import { getSchedule } from 'common/helpers/getSchedule';
import compareObjectsChanges from 'common/helpers/compareObjectsChanges';
import { useTranslation } from 'react-i18next';
import { ClinicScheduleProps } from './modules';

/**
 * A React component that displays the online hours for a clinic.
 *
 * @param {ClinicScheduleProps} props - The props for the component.
 * @returns {JSX.Element} - The JSX element representing the component.
 */
const OnlineHours: FC<ClinicScheduleProps> = ({
  setValue,
  clearErrors,
  enabledWorkingHours,
  hasError,
}) => {
  const { t } = useTranslation();
  const [openingHours, setOpeningHours] = useState<OpeningHours[]>(() =>
    getSchedule({ disableAll: true }),
  );

  useEffect(() => {
    if (!enabledWorkingHours?.length) {
      // If there are no enabled working hours, reset to default schedule with all days disabled.
      setOpeningHours(getSchedule({ disableAll: true }));
    } else {
      // Otherwise, create a new schedule based on the enabled working hours.
      const defaultSchedule = getSchedule({ disableAll: true });

      const result = defaultSchedule.map((day) => {
        return {
          ...day,
          isEnabled: false,
          ...(enabledWorkingHours.find((day2) => day2.day === day.day) || {}),
        };
      });

      setOpeningHours(result);
    }
  }, [enabledWorkingHours]);

  /**
   * Handles adding a new time slot for a given day.
   *
   * @param {string} day - The day of the week to add the time slot to.
   */
  const handleAddSlot = (day: string) => {
    const newSlot = {
      startTime: '',
      endTime: '',
    };

    setOpeningHours((prev) => [
      ...prev.map((item) => {
        if (item.day === day) {
          return {
            ...item,
            slots: [...item.slots, newSlot],
          };
        }
        return item;
      }),
    ]);
  };

  /**
   * Handles editing an existing time slot for a given day.
   *
   * @param {string} day - The day of the week to edit the time slot on.
   * @param {number} index - The index of the time slot to edit.
   * @param {TimeSlots} value - The new value for the time slot.
   */
  const handleEditSlot = (day: string, index: number, value: TimeSlots) => {
    const openingHoursEdited = openingHours?.map((item) => {
      if (item.day === day) {
        return {
          ...item,
          slots: item.slots.map((slot, i) => {
            if (index === i) {
              return { ...slot, ...value };
            }
            return slot;
          }),
        };
      }
      return item;
    });

    setOpeningHours(openingHoursEdited);
  };

  /**
   * Handles enabling or disabling a given day's time slots.
   *
   * @param {string} day - The day of the week to enable/disable the time slots for.
   * @param {boolean} value - Whether the time slots should be enabled (true) or disabled (false).
   */
  const handleEnableSlot = (day: string, value: boolean) => {
    setOpeningHours((prev) =>
      prev.map((item) => {
        if (item.day === day) {
          return {
            ...item,
            isEnabled: value,
          };
        }
        return item;
      }),
    );
  };

  /**
   * Handles removing a time slot for a given day.
   *
   * @param {string} day - The day of the week to remove the time slot from.
   * @param {number} index - The index of the time slot to remove.
   */
  const handleRemoveSlot = (day: string, index: number) => {
    setOpeningHours((prev) =>
      prev.map((item) => {
        if (item.day === day) {
          return {
            ...item,
            slots: item.slots.filter((slot, i) => index !== i),
          };
        }
        return item;
      }),
    );
  };

  /**
   * A memoized value that determines whether the opening hours have changed.
   *
   * @type {boolean}
   */
  const isStateChanged = useMemo(() => {
    const hasChanges = compareObjectsChanges(
      { openingHours },
      {
        openingHours: enabledWorkingHours,
      },
    );
    return hasChanges;
  }, [openingHours, enabledWorkingHours]);

  /**
   * Validates the schedule based on the current opening hours.
   *
   * @param {OpeningHours[]} hours - The current opening hours to validate.
   * @returns {boolean} - True if there are validation errors, false otherwise.
   */
  const scheduleValidation = (hours: OpeningHours[]) => {
    const errorFields = hours.filter((item) =>
      item.slots.some(
        (item) =>
          !item.startTime ||
          !item.endTime ||
          item.startTime === 'Invalid Date' ||
          item.endTime === 'Invalid Date' ||
          item.startTime > item.endTime,
      ),
    );

    return !!errorFields.length;
  };

  /**
   * Handles changes to the opening hours and updates the form value accordingly.
   *
   * @param {OpeningHours[]} hours - The new opening hours to use.
   */
  const handleScheduleChange = (hours: OpeningHours[]) => {
    const newArr = hours.filter((item) => item.isEnabled);
    const hasErrors: boolean = scheduleValidation(newArr);

    setValue('hours', newArr);
    if (!hasErrors) {
      clearErrors('hours');
    }
  };

  useEffect(() => {
    if (openingHours.length && isStateChanged) {
      handleScheduleChange(openingHours);
    }
  }, [openingHours]);

  /**
   * Handles replicating the opening hours for all days based on the Monday schedule.
   */
  const handleAllDays = () => {
    const firstDay = openingHours.find((item) => item.day === 'Monday')!;

    const currentOpeningHours = openingHours.map((day) => {
      if (day.day === 'Saturday' || day.day === 'Sunday') {
        return day;
      }

      return {
        day: day.day,
        isEnabled: firstDay.isEnabled!,
        slots: firstDay.slots!,
      };
    });

    setValue('hours', currentOpeningHours);
    clearErrors('hours');
    setOpeningHours(currentOpeningHours);
  };

  /**
   * A memoized value that determines whether the "replicate" button should be disabled.
   *
   * @type {boolean}
   */
  const isDisabledButton = useMemo(() => {
    if (!openingHours.length) return true;

    const firstDay = openingHours.find((item) => item.day === 'Monday');

    return !firstDay?.slots[0]?.startTime || !firstDay?.slots[0]?.endTime;
  }, [openingHours]);

  return (
    <Wrapper>
      <ScheduleFieldItem>
        <label>{t('professional_profile.subscriptions.online_hours')}</label>
        <span>
          {t('professional_profile.subscriptions.online_hours_description')}
        </span>
        <SlotsContainer>
          <SlotsWrapper>
            {openingHours.map((item, index) => (
              <TimeRange
                key={index}
                day={item.day}
                slots={item.slots}
                isEnabled={item.isEnabled}
                handleAddSlot={handleAddSlot}
                handleEditSlot={handleEditSlot}
                handleRemoveSlot={handleRemoveSlot}
                handleEnableSlot={handleEnableSlot}
                hasError={hasError}
              />
            ))}
          </SlotsWrapper>
          <div>
            <ReplicateButton
              disabled={isDisabledButton}
              onClick={handleAllDays}
            >
              {t('clinicsConfiguration.clinics.replicate')}
            </ReplicateButton>
          </div>
        </SlotsContainer>
      </ScheduleFieldItem>
    </Wrapper>
  );
};

export default OnlineHours;
